import { v4 as uuidv4 } from 'uuid';

/**
 * Services seed data with complete information for all services.
 * Centralized location for service data to avoid duplication.
 */
export const servicesSeedData = [
  {
    id: uuidv4(),
    title: 'AI Advisory',
    shortDescription:
      'Strategic guidance to translate AI potential into measurable business value.',
    description:
      'Transform AI from experimental to essential. We work with C‑suite executives to develop comprehensive AI strategies that deliver competitive advantage, reduce operating costs through automation, and open new revenue opportunities. Our systematic approach sets measurable checkpoints to hold the program accountable to outcomes.',
    slug: 'ai-advisory',
    icon: 'ai',
    keyFeatures: [
      'AI Strategy & Executive Roadmaps',
      'AI Governance & Risk Management',
      'Capability Maturity Assessment',
      'Operating Model Transformation',
      'AI Investment Portfolio Optimization',
      'Change Management & Executive Education',
    ],
    benefits: [
      'Material reduction in operating costs through AI‑enabled automation',
      'New revenue opportunities from AI‑powered products and services',
      'Lower implementation risk via proven governance frameworks',
      'Board‑ready AI strategy with clear accountability',
      'Competitive differentiation through purposeful AI adoption',
      'Clear ROI path defined up front with measurable checkpoints',
    ],
    useCases: [
      'Fortune 500 AI transformation strategy',
      'Board-level AI governance and ethics frameworks',
      'AI capability assessment and competitive analysis',
      'Executive AI education and change management',
      'AI investment prioritization and portfolio management',
      'Regulatory compliance and risk mitigation strategies',
    ],
    outcomes: [
      'Accelerated decision‑making with AI‑powered insights',
      'Improved customer satisfaction via AI‑driven personalization',
      'New revenue opportunities from AI product innovations',
      'Demonstrable compliance with well‑governed AI processes',
    ],
    timeline: {
      discovery:
        '2-3 weeks: Current state assessment, opportunity mapping, stakeholder alignment',
      strategy:
        '4-6 weeks: AI strategy development, governance framework, roadmap creation',
      implementation:
        '8-12 weeks: Pilot program launch, success metrics, scaling plan',
    },
  },
  {
    id: uuidv4(),
    title: 'Data Advisory',
    shortDescription:
      'Data strategy and architecture that makes AI reliable and compliant.',
    description:
      'Turn your data into your competitive moat. We architect enterprise data strategies that lower compliance risk, accelerate time‑to‑insight, and enable data products that create new value. Our frameworks ensure your data foundation scales with AI ambitions while maintaining security and compliance.',
    slug: 'data-advisory',
    icon: 'data',
    keyFeatures: [
      'Enterprise Data Strategy & Product Design',
      'Modern Cloud-Native Data Architecture',
      'Governance, Privacy & Regulatory Compliance',
      'Data Quality & Real-Time Observability',
      'Data Monetization & Product Development',
      'AI-Ready Data Platform Engineering',
    ],
    benefits: [
      'Significant reduction in time‑to‑insight with modern data architecture',
      'Lower compliance risk through automated governance',
      'New value from data product monetization',
      'Real‑time decision‑making with streaming data pipelines',
      'Future‑ready foundation for AI and ML initiatives',
      'Enterprise‑grade security and privacy by design',
    ],
    useCases: [
      'Enterprise data platform modernization for Fortune 1000',
      'GDPR/CCPA compliance automation and privacy engineering',
      'Real-time analytics and decision support systems',
      'Data product development and monetization strategies',
      'AI training data pipelines and feature stores',
      'Multi-cloud data architecture and migration',
    ],
    outcomes: [
      'Reduced data processing costs through cloud optimization',
      'Improved data quality with automated monitoring',
      'Accelerated analytics delivery with modern pipelines',
      'New revenue opportunities enabled by data products',
    ],
    timeline: {
      discovery:
        '2-3 weeks: Data maturity assessment, architecture review, compliance audit',
      design:
        '4-6 weeks: Data strategy, platform architecture, governance framework',
      implementation:
        '8-16 weeks: Platform build, data products, monitoring and optimization',
    },
  },
  {
    id: uuidv4(),
    title: 'Agentic AI Services',
    shortDescription:
      'Design and deploy autonomous agents that deliver measurable outcomes.',
    description:
      'Deploy AI agents that work autonomously while you sleep. Our production‑ready agentic systems handle complex workflows, meaningfully reduce manual effort, and deliver consistent outcomes 24/7. Built with enterprise‑grade safety, evaluation, and observability—your agents operate with confidence and accountability.',
    slug: 'agentic-ai-advisory',
    icon: 'agent',
    keyFeatures: [
      'Production-Ready Agent Architecture',
      'RAG & Tool-Use Implementation',
      'Enterprise Safety & Guardrails',
      'Real-Time Evaluation & Monitoring',
      'MLOps & Production Observability',
      'Human-in-the-Loop Oversight Systems',
    ],
    benefits: [
      'Meaningful reduction in manual workflow processing',
      '24/7 autonomous operation with consistent quality',
      'High decision accuracy through evaluation and guardrails',
      'Scalable from pilot to enterprise deployment',
      'Built‑in safety and compliance monitoring',
      'Clear ROI model with milestones and checkpoints',
    ],
    useCases: [
      'Autonomous customer support with 90% resolution rates',
      'Document processing and intelligent workflow automation',
      'Real-time decision support for operations teams',
      'Compliance monitoring and automated reporting',
      'Supply chain optimization and predictive planning',
      'Financial analysis and risk assessment automation',
    ],
    outcomes: [
      'Lower customer service costs while improving satisfaction',
      'Large‑scale document processing with high accuracy',
      'Automation of routine compliance tasks with auditable oversight',
      'Operational savings through end‑to‑end workflow optimization',
    ],
    timeline: {
      discovery:
        '2-3 weeks: Use case analysis, data readiness, safety requirements',
      pilot: '6-8 weeks: Agent development, guardrails, evaluation framework',
      production:
        '10-14 weeks: Scaling, MLOps integration, performance optimization',
    },
  },
];
